/**
 * Drop-in replacement for the Navigator LockManager lock that @supabase/auth-js
 * installs by default (`auth.lock`).
 *
 * The stock implementation signals "lock is busy" by throwing
 * NavigatorLockAcquireTimeoutError from *inside* the callback handed to
 * `navigator.locks.request()`. GoTrueClient does catch that rejection on the
 * promise it awaits (`_autoRefreshTokenTick` swallows anything with
 * `isAcquireTimeout`), but the browser separately tracks the promise it created
 * for the callback itself. Nothing is attached to that one, so Firefox reports:
 *
 *   Uncaught (in promise) Error: Acquiring an exclusive Navigator LockManager
 *   lock "lock:sb-<ref>-auth-token" immediately failed
 *
 * It is noise rather than a failure — a busy lock just means another tab (or an
 * HMR-orphaned client) is already refreshing the token, and skipping the tick is
 * the correct behaviour. This version reports the busy case by *returning* a
 * sentinel from the callback and throwing after `request()` has settled, so the
 * rejection only ever exists on a promise GoTrueClient is awaiting.
 */

const NOT_AVAILABLE = Symbol('supabase.lock.notAvailable');

/** GoTrueClient treats any error carrying this flag as "lock unavailable". */
class LockUnavailableError extends Error {
  readonly isAcquireTimeout = true;

  constructor(name: string) {
    super(
      `Acquiring an exclusive Navigator LockManager lock "${name}" immediately failed`
    );
    this.name = 'LockUnavailableError';
  }
}

export function supportsNavigatorLocks(): boolean {
  return (
    typeof globalThis !== 'undefined' &&
    typeof globalThis.navigator !== 'undefined' &&
    typeof globalThis.navigator.locks?.request === 'function'
  );
}

/**
 * @param name Lock name.
 * @param acquireTimeout Negative waits indefinitely, 0 fails immediately if the
 *   lock is held, positive aborts the wait after that many milliseconds.
 * @param fn Runs while the lock is held.
 */
export async function quietNavigatorLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> {
  // `ifAvailable` is the only mode that can report "busy", so it is the only
  // one that needs the sentinel dance.
  if (acquireTimeout === 0) {
    const result = await globalThis.navigator.locks.request(
      name,
      { mode: 'exclusive', ifAvailable: true },
      async lock => (lock ? await fn() : NOT_AVAILABLE)
    );

    if (result === NOT_AVAILABLE) {
      throw new LockUnavailableError(name);
    }

    return result as R;
  }

  const abortController = new AbortController();
  const timer =
    acquireTimeout > 0
      ? setTimeout(() => abortController.abort(), acquireTimeout)
      : undefined;

  try {
    return (await globalThis.navigator.locks.request(
      name,
      { mode: 'exclusive', signal: abortController.signal },
      // A spec-compliant browser never passes null here; older ones did, and
      // auth-js chose to run the operation anyway rather than deadlock.
      async () => await fn()
    )) as R;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new LockUnavailableError(name);
    }
    throw error;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
